import { type ReactElement, type ReactNode } from 'react';

import { cn } from '@/shared/libs';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './form/primitives/Select';

const ALL_VALUE = '__all__';

export interface SimpleSelectOption<T extends string = string> {
  icon?: ReactNode;
  label: ReactNode;
  value: T;
}

export interface SimpleSelectProps<T extends string = string> {
  'aria-label'?: string;
  className?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  onValueChange: (value: T) => void;
  options: readonly SimpleSelectOption<T>[];
  placeholder?: string;
  triggerClassName?: string;
  value: T;
}

export const SimpleSelect = <T extends string>({
  'aria-label': ariaLabel,
  className,
  disabled = false,
  fullWidth = false,
  onValueChange,
  options,
  placeholder,
  triggerClassName,
  value,
}: SimpleSelectProps<T>): ReactElement => {
  const resolvedValue = value === '' ? ALL_VALUE : value;

  return (
    <Select
      disabled={disabled}
      value={resolvedValue}
      onValueChange={(next) => {
        onValueChange((next === ALL_VALUE ? '' : next) as T);
      }}>
      <SelectTrigger
        aria-label={ariaLabel}
        className={cn(
          'h-9 min-w-0 rounded-xl border-border/60 bg-background/60',
          fullWidth ? 'w-full' : 'w-full sm:w-auto sm:min-w-[10rem]',
          triggerClassName,
          className,
        )}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem
            key={option.value === '' ? ALL_VALUE : option.value}
            value={option.value === '' ? ALL_VALUE : option.value}>
            <span className="inline-flex min-w-0 items-center gap-1.5">
              {option.icon}
              <span className="truncate">{option.label}</span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

SimpleSelect.displayName = 'SimpleSelect';
